<template>
	<div class="formWrapper">
		<div style="display: inline-flex; align-items: center">
			<div>
				<img v-if="integration.icon" style="width: 40px; margin-right: 10px" :src="`data:image/${integration.iconMimeType === 'png' ? 'png' : 'svg+xml'};base64, ` +
					integration.icon
					" />
				<img style="width: 40px; margin-right: 10px" v-if="!integration.icon" class="nodeIcon"
					src="../assets/node.svg" />
			</div>
			<div>
				<h2>{{ integration.displayName }} Credentials</h2>
			</div>
		</div>
		<p style="margin-top: 0px">
			Configure credentials for <b>{{ integration.displayName }}</b>
			<span v-if="
				integration.resources &&
				integration.resources.credentialDocumentation &&
				integration.resources.credentialDocumentation[0]
			">
				-
				<el-link target="_blank"
					:href="returnDocUrl(integration.resources.credentialDocumentation[0].url.toLowerCase())"
					type="primary">
					Review {{ integration.displayName }} Credential Documentation <i class="el-icon-link"></i>
				</el-link>
			</span>
		</p>
		<el-alert :closable="false" style="margin-top: 20px" title="Credential security" :description="`Your ${splitTexByCase(
			form.credType
		)} credentials are always securely encrypted before being stored`" type="info" show-icon>
		</el-alert>
		<el-divider class="compressedDivider"></el-divider>
		<el-container>
			<el-aside class="credentialControlsWrapper">
				<el-form ref="form" :rules="formRules" label-position="top" label-width="100px" :model="form">
					<el-form-item prop="name" label="Credential Type">
						<el-select @change="returnCredentialDefinition()" no-data-text="No credential type found"
							style="width: 100%" v-model="form.credType" placeholder="Select credential type">
							<el-option v-for="type in this.integration.credentials" :key="type.name"
								:label="splitTexByCase(type.name)" :value="type.name">
							</el-option>
						</el-select>
					</el-form-item>
					<el-form-item prop="name" :label="`Existing Credentials - ${splitTexByCase(form.credType)}`">
						<el-select v-loading="interfaceLoading" clearable :no-data-text="`No existing credentials`"
							style="width: 100%" v-model="form.existingCredentialName"
							placeholder="Select existing credential">
							<el-option v-for="cred in this.existingCredentialsForType" :key="cred.id" :label="cred.name"
								:value="cred.name">
							</el-option>
						</el-select>
					</el-form-item>
					<el-alert v-if="form.existingCredentialName && !isCredentialOwner && selectedCredentialOwner"
						type="info" show-icon :closable="false" style="margin-bottom: 15px"
						:title="`This credential is owned by ${selectedCredentialOwner}`"
						description="You can use this credential in your workflows, but you cannot view or edit its values for security reasons." />
					<el-form-item v-if="form.existingCredentialName && isCredentialOwner">
						<el-button @click="deleteCred()" style="width: 100%; margin-top: 12px" type="danger"
							:loading="operationInProgress">Delete Credential
						</el-button>
						<el-button v-if="this.credentialInterface.isDefault != true" @click="defaultCred()"
							style="width: 100%; margin-top: 12px; margin-left: 0;" type="primary"
							:loading="defaultOperationInProgress">Set as Default Credential
						</el-button>
						<el-button style="width: 100%; margin-top: 12px; margin-left: 0;" type="primary"
							@click="openShareCredentialModal">
							Share Credential
						</el-button>

						<el-dialog title="Share Credential" :visible.sync="shareCredentialDialog" width="600px"
							append-to-body :modal-append-to-body="false">
							<shareCredentialModal :credential="credentialInterface"
								@close="shareCredentialDialog = false" :key="credentialInterface.id" />
						</el-dialog>
					</el-form-item>
				</el-form>
			</el-aside>
			<el-main v-loading="interfaceLoading || singleInterfaceLoading" class="credentialInterfaceWrapper">
				<credentialsInterfaceForm @credentialCreated="credentialCreated" @credentialUpdated="credentialUpdated"
					:isExistingCredential="credentialInterface && credentialInterface.id ? true : false"
					:credentialInterface="credentialInterface" :isOwner="isCredentialOwner" />
			</el-main>
		</el-container>
	</div>
</template>

<script>
import credentialsInterfaceForm from "./credentialsInterfaceForm.vue";
import shareCredentialModal from "./shareCredentialModal.vue";
import { splitTexByCase } from "../utils/general.js";
import { normalizeFields } from "../utils/normalizeFields.js";
import _ from "lodash";
export default {
	components: {
		credentialsInterfaceForm,
		shareCredentialModal
	},
	props: {
		integration: Object,
	},
	computed: {
		existingCredentialNameChosen() {
			return this.form.existingCredentialName;
		},
		isCredentialOwner() {
			const selected = this.existingCredentialsForType.find(
				c => c.name === this.form.existingCredentialName
			);

			return selected ? selected?.isOwner === true : true;
		},
		selectedCredentialOwner() {
			const selected = this.existingCredentialsForType.find(
				c => c.name === this.form.existingCredentialName
			);

			return selected?.userName || null;
		},
	},
	data() {
		return {
			credentialUpdatedFlag: false,
			credentialInterface: {},
			existingCredentialsForType: [],
			interfaceLoading: true,
			singleInterfaceLoading: true,
			credentialDefinition: {},
			operationInProgress: false,
			defaultOperationInProgress: false,
			form: { credType: "", existingCredentialName: "" },
			formRules: {},
			shareCredentialDialog: false,
		};
	},
	mounted() {
		this.form.credType = this.integration.credentials[0]?.name;
		this.returnCredentialDefinition();
	},
	watch: {
		existingCredentialNameChosen: function () {
			if (this.form.existingCredentialName && !this.credentialUpdatedFlag) {
				this.singleInterfaceLoading = true;
				const relevantCred = this.existingCredentialsForType.find((each) => {
					return each.name === this.form.existingCredentialName;
				});
				this.credentialInterface = _.cloneDeep(relevantCred);
				this.stopInterfaceLoading();
			} else if (!this.credentialUpdatedFlag) {
				this.returnCredentialDefinition(true);
			}
		},
		integration: function () {
			this.form.credType = this.integration.credentials[0]?.name;
			this.returnCredentialDefinition();
		},
	},
	methods: {
		openShareCredentialModal() {
			this.shareCredentialDialog = true;
		},
		returnDocUrl(baseUrl) {
			baseUrl = baseUrl.replace("credentials/", "");
			return `${process.env.VUE_APP_DOCS_BASE_URL}${baseUrl}`;
		},
		deleteCred() {
			this.$confirm("Do you wish to permanently delete this credential. Continue?", "Warning", {
				confirmButtonText: "OK",
				cancelButtonText: "Cancel",
				type: "warning",
			})
				.then(async () => {
					const result = await this.httpHelper("RUNTIME", "DELETE", "credentials/deleteOne", {
						credentialId: this.credentialInterface.id,
					});
					if (!result?.error) {
						this.credentialUpdatedFlag = true;
						this.form.existingCredentialName = "";
						this.credentialUpdatedFlag = false;
						this.existingCredentialsForType = this.existingCredentialsForType.filter((each) => {
							return each.id !== this.credentialInterface.id;
						});
						this.$notify({
							title: "Success",
							message: "Credential deleted",
							type: "success",
							position: "bottom-left",
						});
					} else {
						this.$notifyError(this, result.error);
					}
				})
				.catch(() => { });
		},
		defaultCred() {
			this.$confirm("Do you wish to set this credential as default. Continue?", "Warning", {
				confirmButtonText: "OK",
				cancelButtonText: "Cancel",
				type: "warning",
			})
				.then(async () => {
					const selectedCredential = this.existingCredentialsForType.find(
						each => each.id === this.credentialInterface.id
					);
					const payload = JSON.parse(JSON.stringify(selectedCredential));

					const result = await this.httpHelper("RUNTIME", "PUT", "credentials/updateOne", {
						credentialId: this.credentialInterface.id,
						isDefault: true,
						data: payload.data,
						name: payload.name,
						type: payload.type,
					});

					if (!result?.error) {
						await this.returnExistingCredentialsForIntegration();
						this.credentialUpdatedFlag = true;
						this.form.existingCredentialName = "";
						this.credentialUpdatedFlag = false;
						const relevantCred = this.existingCredentialsForType.find((each) => {
							return each.name === this.form.existingCredentialName;
						});
						this.credentialInterface = _.cloneDeep(relevantCred);
						this.$notify({
							title: "Success",
							message: "Credential updated",
							type: "success",
							position: "bottom-left",
						});
					} else {
						this.$notifyError(this, result.error);
					}
				})
				.catch(() => { });
		},
		async credentialCreated() {
			await this.returnExistingCredentialsForIntegration();

			this.form.existingCredentialName =
				this.existingCredentialsForType[this.existingCredentialsForType.length - 1].name;
		},
		credentialUpdated(form) {
			this.credentialUpdatedFlag = true;
			this.form.existingCredentialName = form.name;
			this.credentialUpdatedFlag = false;
			this.existingCredentialsForType = this.existingCredentialsForType.map((each) => {
				if (each.id === form.credentialId) {
					return _.cloneDeep({
						...each,
						...form,
					});
				} else {
					return each;
				}
			});
		},
		splitTexByCase(text) {
			return splitTexByCase(text);
		},
		stopInterfaceLoading() {
			setTimeout(() => {
				this.interfaceLoading = false;
				this.singleInterfaceLoading = false;
			}, 500);
		},
		async returnExistingCredentialsForIntegration() {
			const result = await this.httpHelper("RUNTIME", "GET", "credentials/returnAllCredentialsOfSpecificType", {
				type: this.form.credType,
			});
			if (!result?.error) {
				this.existingCredentialsForType = result.response || [];
			} else {
				this.$notifyError(this, result.error, "An error occurred when trying to retrieve existing credentials");
			}
		},
		async returnCredentialDefinition(dontRefetchExistingCreds) {
			this.interfaceLoading = !dontRefetchExistingCreds ? true : false;
			this.singleInterfaceLoading = true;
			this.form.existingCredentialName = "";
			const result = await this.httpHelper("RUNTIME", "GET", "credentials/returnSpecificCredentialDefinition", {
				name: this.form.credType,
			});
			if (!result?.error) {
				this.credentialDefinition = result.response;
				if (!dontRefetchExistingCreds) {
					await this.returnExistingCredentialsForIntegration();
				}
				this.credentialDefinition.properties = normalizeFields([...this.credentialDefinition.properties]);
				this.credentialInterface = this.credentialDefinition;
				this.stopInterfaceLoading();
			}
		},
	},
};
</script>

<style scoped>
.credentialControlsWrapper {
	padding: 0px;
	padding-bottom: 20px;
	margin-right: 40px;
	width: 40%;
	padding-top: 24px;
}

.credentialInterfaceWrapper {
	padding: 0px;
	padding-bottom: 20px;
	padding-right: 20px;
	padding-top: 24px;
}

.compressedDivider {
	margin-bottom: 0px;
}
</style>
